

import api from "../lib/api";
import { handleError } from "../lib/errors";
import { LIMITS, ERROR_MESSAGES } from "../lib/constants";


export interface Meeting {
  id: string;
  number: number;
  date: string;
  next_date?: string | null;
  location?: string;
  status: "draft" | "final" | "rascunho" | "em_andamento" | "finalizado";
  project_id: string;
  created_at?: string;
  updated_at?: string;
  name?: string;
  observacoes_finais?: string | null;
  proxima_reuniao_texto?: string | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
}


interface BackendAta {
  id: number;
  numero: number;
  data: string | Date;
  proxima_reuniao?: string | Date | null;
  localizacao: string;
  status: "rascunho" | "em_andamento" | "finalizado";
  projeto_id: number;
  criado_em?: string | Date;
  atualizado_em?: string | Date;
  nome?: string;
  observacoes_finais?: string | null;
  proxima_reuniao_texto?: string | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
}


function mapBackendToFrontend(backend: BackendAta): Meeting {
  const normalizeStatus = (status: string): Meeting["status"] => {
    if (status === "rascunho" || status === "draft") return "draft";
    if (status === "em_andamento") return "draft";
    if (status === "finalizado" || status === "final") return "final";
    return "draft";
  };

  const formatDate = (
    date: string | Date | null | undefined
  ): string | null => {
    if (!date) return null;
    

    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    

    const dateObj = date instanceof Date ? date : new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    id: backend.id.toString(),
    number: backend.numero,
    date: formatDate(backend.data) || (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    next_date: formatDate(backend.proxima_reuniao),
    location: backend.localizacao,
    status: normalizeStatus(backend.status),
    project_id: backend.projeto_id.toString(),
    created_at: backend.criado_em
      ? typeof backend.criado_em === "string"
        ? backend.criado_em
        : backend.criado_em.toISOString()
      : undefined,
    updated_at: backend.atualizado_em
      ? typeof backend.atualizado_em === "string"
        ? backend.atualizado_em
        : backend.atualizado_em.toISOString()
      : undefined,
    name: backend.nome,
    observacoes_finais: backend.observacoes_finais ?? null,
    proxima_reuniao_texto: backend.proxima_reuniao_texto ?? null,
    hora_inicio: backend.hora_inicio ?? null,
    hora_fim: backend.hora_fim ?? null,
  };
}


function mapFrontendToBackend(frontend: Partial<Meeting>): Partial<BackendAta> {
  const normalizeStatus = (
    status?: string
  ): "rascunho" | "em_andamento" | "finalizado" => {
    if (status === "final") return "finalizado";
    if (status === "draft") return "rascunho";
    return "rascunho";
  };

  const payload: Partial<BackendAta> = {};

  if (frontend.number !== undefined) payload.numero = frontend.number;
  if (frontend.date !== undefined) payload.data = frontend.date;
  if (frontend.next_date !== undefined)
    payload.proxima_reuniao = frontend.next_date || null;
  if (frontend.location !== undefined) payload.localizacao = frontend.location;
  if (frontend.status !== undefined)
    payload.status = normalizeStatus(frontend.status);
  if (frontend.project_id !== undefined)
    payload.projeto_id = Number(frontend.project_id);
  if (frontend.name !== undefined) payload.nome = frontend.name;
  if (frontend.observacoes_finais !== undefined) payload.observacoes_finais = frontend.observacoes_finais;
  if (frontend.proxima_reuniao_texto !== undefined) payload.proxima_reuniao_texto = frontend.proxima_reuniao_texto;
  if (frontend.hora_inicio !== undefined) payload.hora_inicio = frontend.hora_inicio;
  if (frontend.hora_fim !== undefined) payload.hora_fim = frontend.hora_fim;

  return payload;
}


function validateMeetingInput(input: {
  project_id: string;
  number: number;
  date: string;
  location?: string;
  status?: "draft" | "final";
  name?: string;
}): { valid: boolean; error?: string } {

  const numericProjectId = Number(input.project_id);
  if (isNaN(numericProjectId) || numericProjectId <= 0) {
    return { valid: false, error: "ID do projeto inválido" };
  }


  if (typeof input.number !== "number" || input.number < 1) {
    return { valid: false, error: "Número da ata deve ser um número positivo (ex: 1, 2, 3...)" };
  }


  if (!input.date) {
    return { valid: false, error: "Data da reunião é obrigatória. Selecione a data em que a reunião ocorreu." };
  }
  const dateObj = new Date(input.date);
  if (isNaN(dateObj.getTime())) {
    return { valid: false, error: "Data inválida. Use o formato correto (ex: 2026-01-15)." };
  }

  // Validar localização
  if (input.location && input.location.trim().length > LIMITS.MEETING_LOCATION_MAX) {
    return { valid: false, error: `Localização excedeu o limite de ${LIMITS.MEETING_LOCATION_MAX} caracteres.` };
  }

  // Validar nome
  if (input.name && input.name.trim().length > LIMITS.MEETING_NAME_MAX) {
    return { valid: false, error: `Nome da ata excedeu o limite de ${LIMITS.MEETING_NAME_MAX} caracteres.` };
  }







  return { valid: true };
}

export interface UserPermission {
  level: "view" | "edit" | "owner";
  canEdit: boolean;
  canDelete: boolean;
  canInvite: boolean;
  isOwner: boolean;
}

export interface ProjectMeetingsResponse {
  meetings: Meeting[];
  permission: UserPermission;
}

export async function getProjectMeetings(
  projectId: string
): Promise<ProjectMeetingsResponse> {
  try {

    const numericId = Number(projectId);
    if (isNaN(numericId)) {
      throw new Error("ID do projeto inválido");
    }


    const response = await api.get(`/projetos/${numericId}/atas`);

    // Suportar novo formato (com permission) e antigo (só array)
    if (response.data && response.data.atas) {
      const backendAtas = Array.isArray(response.data.atas) ? response.data.atas : [];
      return {
        meetings: backendAtas.map(mapBackendToFrontend),
        permission: response.data.permission || {
          level: "view",
          canEdit: false,
          canDelete: false,
          canInvite: false,
          isOwner: false,
        },
      };
    }

    // Fallback para formato antigo (apenas array)
    const backendAtas = Array.isArray(response.data) ? response.data : [];
    return {
      meetings: backendAtas.map(mapBackendToFrontend),
      permission: {
        level: "owner",
        canEdit: true,
        canDelete: true,
        canInvite: true,
        isOwner: true,
      },
    };
  } catch (error) {
    const message = handleError(error, "getProjectMeetings");
    throw new Error(message);
  }
}


export async function getMeetingById(meetingId: string): Promise<Meeting> {
  try {

    const numericId = Number(meetingId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const response = await api.get(`/atas/${numericId}`);
    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "getMeetingById");
    throw new Error(message);
  }
}


export async function createMeeting(input: {
  project_id: string;
  number: number;
  date: string;
  location?: string;
  status?: "draft" | "final";
  name?: string;
}): Promise<Meeting> {
  try {

    const validation = validateMeetingInput(input);
    if (!validation.valid) {
      throw new Error(validation.error || "Dados de entrada inválidos");
    }


    const numericProjectId = Number(input.project_id);

    const payload = mapFrontendToBackend({
      project_id: input.project_id,
      number: input.number,
      date: input.date,
      location: input.location?.trim() || "Local a definir",
      status: input.status || "draft",
      name: input.name,
    });



    const payloadWithProjectId = {
      ...payload,
      projeto_id: numericProjectId,
    };

    const response = await api.post("/atas", payloadWithProjectId);


    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao criar ata");
    }

    return mapBackendToFrontend(response.data);
  } catch (error: any) {


    if (error instanceof Error) {
      const errorMessage = error.message;

      if (errorMessage.includes("Tempo limite excedido")) {
        throw error;
      }
      if ((error as any).code === "ECONNABORTED" || errorMessage.includes("timeout")) {
        throw new Error("Tempo limite excedido. Por favor, tente novamente.");
      }

      if (
        errorMessage.includes("ID da ata inválido") ||
        errorMessage.includes("ID do projeto inválido") ||
        errorMessage.includes("Resposta inválida do servidor")
      ) {
        throw error;
      }

      if (
        errorMessage.includes("Dados de entrada inválidos") ||
        errorMessage.includes("Número da ata deve ser") ||
        errorMessage.includes("Data da reunião é obrigatória")
      ) {
        throw error;
      }
    }
    const message = handleError(error, "createMeeting");
    throw new Error(message);
  }
}


export async function updateMeeting(
  meetingId: string,
  input: Partial<Omit<Meeting, "id" | "project_id">>
): Promise<Meeting> {
  try {

    const numericId = Number(meetingId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }


    if (input.location !== undefined && input.location.trim().length >= LIMITS.MEETING_LOCATION_MAX) {
      throw new Error(`Localização excedeu o limite de ${LIMITS.MEETING_LOCATION_MAX} caracteres. Reduza o texto para poder salvar.`);
    }


    if (input.date !== undefined) {
      const dateObj = new Date(input.date);
      if (isNaN(dateObj.getTime())) {
        throw new Error("Data inválida. Use o formato correto (ex: 2026-01-15).");
      }
    }

    const payload = mapFrontendToBackend(input);
    const response = await api.put(`/atas/${numericId}`, payload);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao atualizar ata");
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "updateMeeting");
    throw new Error(message);
  }
}


export async function cloneMeeting(
  meetingId: string,
  input?: { date?: string; number?: number }
): Promise<Meeting> {
  try {

    const numericId = Number(meetingId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }


    if (input?.date) {
      const dateObj = new Date(input.date);
      if (isNaN(dateObj.getTime())) {
        throw new Error("Data inválida");
      }
    }

    const payload: { data?: string; numero?: number } = {};
    if (input?.date) payload.data = input.date;
    if (input?.number) payload.numero = input.number;

    const response = await api.post(`/atas/${numericId}/clonar`, payload);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao clonar ata");
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "cloneMeeting");
    throw new Error(message);
  }
}


export async function deleteMeeting(meetingId: string): Promise<void> {
  try {

    const numericId = Number(meetingId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    await api.delete(`/atas/${numericId}`);
  } catch (error) {
    const message = handleError(error, "deleteMeeting");
    throw new Error(message);
  }
}


// Interface para o endpoint agregado /atas/:id/editor-data
export interface MeetingEditorData {
  meeting: Meeting;
  project: {
    id: number;
    nome: string;
    descricao: string | null;
    cover_image: string | null;
  };
  topicos: Array<{
    id: string;
    titulo: string;
    ordem: number;
    ata_id: string;
  }>;
  items: Array<{
    id: string;
    descricao: string;
    concluido: boolean;
    ata_id: string;
    topico_id?: string;
    entidade?: string;
    prazo?: string;
    ata_origem_numero?: number;
    ata_origem_data?: string;
  }>;
  participantes: Array<{
    id: string;
    nome: string;
    empresa?: string;
    cargo?: string;
    email?: string;
    ata_id: string;
  }>;
  lastMeetingNumber: number;
  permission?: {
    level: "view" | "edit" | "owner";
    canEdit: boolean;
    canDelete: boolean;
    canInvite: boolean;
    isOwner: boolean;
  };
}

/**
 * Endpoint agregado otimizado para carregar todos os dados do editor de ata
 *
 * Performance: 6 requests HTTP -> 1 request | ~70-80% mais rápido
 *
 * Substitui as seguintes chamadas:
 * - getMeetingById()
 * - getProjectById()
 * - getTopicosByAtaId()
 * - getItemsByAtaId()
 * - getParticipantesByAtaId()
 * - getProjectMeetings() (apenas para lastMeetingNumber)
 */
export async function getMeetingEditorData(
  meetingId: string
): Promise<MeetingEditorData> {
  try {
    const numericId = Number(meetingId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const response = await api.get(`/atas/${numericId}/editor-data`);
    return response.data;
  } catch (error) {
    const message = handleError(error, "getMeetingEditorData");
    throw new Error(message);
  }
}
